'use client';

import React, { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Discount } from '@/widgets/DoctorCard';
import { useDragScroll } from '@/shared/hooks/dom/scroll/useDragScroll';
import { useElementScroll } from '@/shared/hooks/dom/scroll/useElementScroll';
import { useScrollVisibility } from '@/shared/hooks/dom/scroll/useScrollVisibility';
import { classNames } from '@/shared/lib/classnames/classnames';
import { ScrollShadow } from '@/shared/ui/scroll-shadow';
import type { IMedcenter } from '@/entities/medcenter/model/types';
interface Props {
  medcenters: IMedcenter[];
  defaultActiveIndex: number;
  medcenterClick: (medcenter: IMedcenter) => void;
}
export const MedcenterTabs: React.FC<Props> = ({
  medcenters,
  defaultActiveIndex,
  medcenterClick
}) => {
  const t = useTranslations('doctor.price');
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    showLeftControl,
    showRightControl
  } = useScrollVisibility(containerRef);
  const {
    scrollToElement
  } = useElementScroll(containerRef);
  const {
    dragHandlers,
    isDragging,
    dragThresholdMet
  } = useDragScroll(containerRef, {
    dragSpeedMultiplier: 1.5,
    dragThreshold: 5
  });
  const handleClick = (index: number) => {
    if (isDragging || dragThresholdMet) return;
    setActiveIndex(index);
    scrollToElement(index);
    medcenterClick(medcenters[index]);
  };
  return <div className="container mx-auto" data-sentry-component="MedcenterTabs" data-sentry-source-file="index.tsx">
      <div className="relative">
        <div ref={containerRef} {...dragHandlers} className={classNames('flex items-start overflow-x-auto scroll-width-none', {
        'cursor-grabbing': isDragging,
        'cursor-grab': !isDragging
      })}>
          {medcenters.map((medcenter, index) => <button key={index} className={classNames('relative mr-2 flex shrink-0 cursor-pointer items-center gap-2 rounded-2lg border border-solid p-2 transition-all duration-300', {
          'border-additionalBlue': index === activeIndex,
          'border-borderSecondary bg-bgPrimary hover:border-additionalBlue/50 hover:bg-additionalBlue/10 active:border-additionalBlue/70 active:bg-additionalBlue/20': index !== activeIndex
        })} onClick={() => handleClick(index)}>
              {medcenter.appointments.primary.discountPercentage && <Discount color="gradient">
                  {t('discount', {
              discount: medcenter.appointments.primary.discountPercentage
            })}
                </Discount>}
              <span className="block max-w-[30ch] truncate text-center text-base font-medium text-textPrimary">
                {medcenter.name}
              </span>
            </button>)}
        </div>

        <ScrollShadow showLeftShadow={showLeftControl} showRightShadow={showRightControl} data-sentry-element="ScrollShadow" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
};